/**
 * Quixo staff access requests (flag identity.staff_access_approval; docs/runbooks/staff-access-approval.md).
 * The workspace owner approves or declines Quixo staff access to this workspace and can end an active grant any time.
 * Shown in Settings > Security (/settings/security). The section stays hidden when the API answers 403 (not the owner)
 * or 404, or reports enabled:false (switch off). Strings live in this folder's own EN/AR catalog.
 */
import { useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Field, Heading, Panel, Text, TextInput } from '@qm/ui';
import type { Locale } from '@qm/i18n';
import { useApp } from '../../../lib/app-context';
import { bff, BffError, type FetchLike } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import en from './i18n/en.json';
import ar from './i18n/ar.json';

export const STAFF_ACCESS_CATALOG = { en, ar } as const;
type Key = keyof typeof en;
type Params = Record<string, string | number>;

/** Translator over this folder's catalog ({name} placeholders). */
export function staffAccessT(locale: Locale) {
  const cat = (STAFF_ACCESS_CATALOG[locale] ?? en) as Record<string, string>;
  return (key: string, params: Params = {}) => {
    const k = `customer.staffAccess.${key}` as Key;
    const raw = cat[k] ?? (en as Record<string, string>)[k] ?? k;
    return raw.replace(/\{(\w+)\}/g, (m, p: string) => (p in params ? String(params[p]) : m));
  };
}

export interface StaffAccessItem {
  id: string; scope: 'read' | 'write' | 'view_as'; reason: string; durationHours: number; status: string;
  requestedAt: string; requestExpiresAt: string; expiresAt: string | null;
  staff: { name: string | null; email: string }; target: { type: 'tenant' | 'partner'; id: string; name?: string | null };
}
export interface StaffAccessList { enabled: boolean; items: StaffAccessItem[]; pending?: number }

export function decideStaffAccess(id: string, verb: 'approve' | 'decline' | 'revoke', note = '', fetchFn?: FetchLike) {
  return bff<StaffAccessItem>(`/v1/identity/access-requests/${encodeURIComponent(id)}/${verb}`, { method: 'POST', body: JSON.stringify(note.trim() ? { note: note.trim() } : {}) }, fetchFn);
}

/** Hidden for non-owners (403), when identity has no such route (404) and while the switch is off. */
export const isHiddenError = (e: unknown) => e instanceof BffError && (e.status === 403 || e.status === 404);

export function StaffAccessSection() {
  const { locale } = useApp();
  const t = staffAccessT(locale);
  const res = useResource<StaffAccessList>('/v1/identity/access-requests');
  if (res.status === 'loading') return null;
  const hidden = (res.status === 'error' && isHiddenError(res.error)) || (res.status === 'ready' && !res.data.enabled);
  if (hidden) return null;
  if (res.status === 'error') return <ErrorState message={t('failed')} />;
  return <StaffAccessPanel items={res.data.items} reload={res.reload} />;
}

export function StaffAccessPanel({ items, reload }: { items: readonly StaffAccessItem[]; reload: () => void }) {
  const { locale } = useApp();
  const t = staffAccessT(locale);
  return (
    <Panel data-testid="staff-access-section">
      <Heading level={2}>{t('title')}</Heading>
      <Text tone="muted">{t('intro')}</Text>
      {items.length ? items.map((i) => <StaffAccessCard key={i.id} item={i} reload={reload} />) : <EmptyState title={t('none')} />}
    </Panel>
  );
}

const TONE: Record<string, 'neutral' | 'accent' | 'warning' | 'error' | 'success'> = { pending: 'warning', active: 'success', declined: 'error', revoked: 'error' };

export function StaffAccessCard({ item, reload }: { item: StaffAccessItem; reload: () => void }) {
  const { locale } = useApp();
  const t = staffAccessT(locale);
  const f = useFormat();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const act = async (verb: 'approve' | 'decline' | 'revoke') => {
    setBusy(true); setFailed(false);
    try { await decideStaffAccess(item.id, verb, note); reload(); } catch { setFailed(true); } finally { setBusy(false); }
  };
  const who = item.staff.name ? `${item.staff.name} (${item.staff.email})` : item.staff.email;
  const open = item.status === 'pending' || item.status === 'active';
  return (
    <article data-testid={`staff-access-${item.id}`}>
      <Heading level={3}>{t('asked', { who })}</Heading>
      <Badge tone={TONE[item.status] ?? 'neutral'}>{t(`status.${item.status}`)}</Badge>
      <Text>{t('level')}: {t(`scope.${item.scope}`)}</Text>
      <Text>{t('reason')}: <bdi>{item.reason}</bdi></Text>
      <Text tone="muted">{t('duration', { hours: item.durationHours })}</Text>
      {item.status === 'active' && item.expiresAt ? <Text tone="muted">{t('endsAt', { when: f.dateTime(item.expiresAt) })}</Text> : null}
      {open ? (
        <>
          <Field label={t('note')}>
            {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} name="note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          {item.status === 'pending' ? (
            <>
              <Button variant="primary" disabled={busy} onClick={() => void act('approve')} data-testid={`staff-access-approve-${item.id}`}>{t('approve')}</Button>
              <Button disabled={busy} onClick={() => void act('decline')} data-testid={`staff-access-decline-${item.id}`}>{t('decline')}</Button>
            </>
          ) : (
            <Button disabled={busy} onClick={() => void act('revoke')} data-testid={`staff-access-revoke-${item.id}`}>{t('revoke')}</Button>
          )}
        </>
      ) : null}
      {failed ? <ErrorState message={t('failed')} /> : null}
    </article>
  );
}
